import { useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Phone, Mail, Check } from "lucide-react";
import { EVENT_TYPES, CONTACT, BUDGET_OPTIONS } from "../../lib/content";
import { Eyebrow } from "../Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = { name: "", email: "", phone: "", event_type: "", group_size: "", event_date: "", rooms: "", budget: "", message: "" };

export const Contact = () => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [human, setHuman] = useState(false);
  const [honeypot, setHoneypot] = useState("");
  const startRef = useRef(Date.now());

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.event_type) {
      toast.error("Please fill in your name, email, and event type.");
      return;
    }
    if (!human) {
      toast.error("Please confirm you're not a robot.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/inquiries`, {
        ...form,
        website: honeypot,
        elapsed_ms: Date.now() - startRef.current,
        human_verified: human,
      });
      toast.success("Request received — we'll be in touch shortly.");
      setForm(EMPTY);
      setHuman(false);
      startRef.current = Date.now();
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Something went wrong. Please call or email us directly.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-border py-3 text-base placeholder:text-muted-foreground/60 focus:border-primary focus:outline-none transition-colors";

  return (
    <section id="contact" data-testid="contact" className="bg-secondary text-secondary-foreground py-24 md:py-32">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-24">
        <div>
          <Eyebrow className="text-primary">Request services</Eyebrow>
          <h2 className="font-display text-5xl md:text-7xl font-light tracking-tight leading-[0.95] mt-4 mb-8">
            Let's block
            <br />
            <span className="italic">your rooms.</span>
          </h2>
          <p className="text-white/70 font-light leading-relaxed max-w-md mb-12">
            Tell us about your group and we'll come back with hotel and venue options, group rates, and
            a plan to keep everyone together.
          </p>

          <div className="space-y-5">
            <a data-testid="contact-phone" href={CONTACT.phoneHref} className="flex items-center gap-4 group">
              <span className="w-11 h-11 border border-white/25 flex items-center justify-center group-hover:bg-primary group-hover:border-primary transition-colors">
                <Phone className="w-4 h-4" />
              </span>
              <span className="font-display text-2xl md:text-3xl font-light group-hover:text-primary transition-colors">
                {CONTACT.phone}
              </span>
            </a>
            <a data-testid="contact-email" href={CONTACT.emailHref} className="flex items-center gap-4 group">
              <span className="w-11 h-11 border border-white/25 flex items-center justify-center group-hover:bg-primary group-hover:border-primary transition-colors">
                <Mail className="w-4 h-4" />
              </span>
              <span className="text-base md:text-lg font-light group-hover:text-primary transition-colors break-all">
                {CONTACT.email}
              </span>
            </a>
          </div>
        </div>

        <form data-testid="inquiry-form" onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div className="sm:col-span-2">
            <label className="label-eyebrow text-white/60">Full name *</label>
            <input data-testid="input-name" className={inputCls} value={form.name} onChange={set("name")} placeholder="Jane Doe" />
          </div>
          <div>
            <label className="label-eyebrow text-white/60">Email *</label>
            <input data-testid="input-email" type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="jane@email.com" />
          </div>
          <div>
            <label className="label-eyebrow text-white/60">Phone</label>
            <input data-testid="input-phone" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="(000) 000-0000" />
          </div>
          <div className="sm:col-span-2">
            <label className="label-eyebrow text-white/60">Event type *</label>
            <select data-testid="input-event-type" className={`${inputCls} appearance-none`} value={form.event_type} onChange={set("event_type")}>
              <option value="" className="text-foreground">Select an event type</option>
              {EVENT_TYPES.map((t) => (
                <option key={t} value={t} className="text-foreground">{t}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label-eyebrow text-white/60">Approx. group size</label>
            <input data-testid="input-group-size" className={inputCls} value={form.group_size} onChange={set("group_size")} placeholder="e.g. 40 guests" />
          </div>
          <div>
            <label className="label-eyebrow text-white/60">Dates</label>
            <input data-testid="input-event-date" className={inputCls} value={form.event_date} onChange={set("event_date")} placeholder="e.g. Oct 2026" />
          </div>
          <div>
            <label className="label-eyebrow text-white/60">Rooms needed</label>
            <input data-testid="input-rooms" className={inputCls} value={form.rooms} onChange={set("rooms")} placeholder="e.g. 25 rooms" />
          </div>
          <div>
            <label className="label-eyebrow text-white/60">Estimated budget</label>
            <select data-testid="input-budget" className={`${inputCls} appearance-none`} value={form.budget} onChange={set("budget")}>
              <option value="" className="text-foreground">Select a range</option>
              {BUDGET_OPTIONS.map((b) => (
                <option key={b} value={b} className="text-foreground">{b}</option>
              ))}
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className="label-eyebrow text-white/60">Tell us more</label>
            <textarea data-testid="input-message" rows={3} className={`${inputCls} resize-none`} value={form.message} onChange={set("message")} placeholder="Location preferences, meeting space, budget…" />
          </div>

          {/* Honeypot: hidden from real users, catches bots */}
          <div className="hidden" aria-hidden="true">
            <label>Company (leave blank)</label>
            <input
              tabIndex={-1}
              autoComplete="off"
              value={honeypot}
              onChange={(e) => setHoneypot(e.target.value)}
            />
          </div>

          <button
            type="button"
            data-testid="not-a-robot"
            onClick={() => setHuman((v) => !v)}
            className="sm:col-span-2 mt-2 flex items-center gap-3 group w-fit"
          >
            <span
              className={`w-6 h-6 border flex items-center justify-center transition-colors ${
                human ? "bg-primary border-primary" : "border-white/40 group-hover:border-white/70"
              }`}
            >
              {human ? <Check className="w-4 h-4 text-primary-foreground" /> : null}
            </span>
            <span className="label-eyebrow text-white/70">I'm not a robot</span>
          </button>

          <button
            data-testid="submit-inquiry-btn"
            type="submit"
            disabled={loading || !human}
            className="sm:col-span-2 mt-1 bg-primary text-primary-foreground py-4 label-eyebrow flex items-center justify-center gap-2 hover:bg-[#a04d2d] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            {loading ? "Sending" : "Send request"}
          </button>
        </form>
      </div>
    </section>
  );
};
